import type { ReactNode } from "react";
import { Checkbox, Column, Row } from "@hightouch/htui";

/**
 * Selectable media card shell: content fills the card, with a frosted header
 * overlay (always-visible bulk-select checkbox on the left, optional actions on
 * the right) and an optional frosted footer overlay. Selection renders as the
 * standard 2px primary outline (mirrors SelectableCard's selected treatment).
 *
 * Used by the candidates grid (CandidateCard) and the campaign intake's
 * additional-assets picker (product/image cards).
 */
type Props = {
  /** Whether this card is part of the current selection. */
  selected: boolean;
  /** Toggles selection when the checkbox changes. */
  onToggleSelect: () => void;
  checkboxDisabled?: boolean;
  /**
   * Screen-reader name for the checkbox, e.g. the asset's title. Without it
   * every card's checkbox reads as the generic "Select checkbox".
   */
  checkboxLabel?: string;
  /**
   * Hide the bulk-select checkbox (single-select pickers). Selection still
   * shows via the card outline and the card body still toggles on click.
   */
  hideCheckbox?: boolean;
  /** Right side of the frosted header (toggles, menus). */
  actions?: ReactNode;
  /** Content of the frosted footer overlay. Omitted → no footer. */
  footer?: ReactNode;
  /** Card media — fills the card behind the header/footer overlays. */
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  height?: string | number;
};

export const SelectableAssetCard = ({
  selected,
  onToggleSelect,
  checkboxDisabled = false,
  checkboxLabel,
  hideCheckbox = false,
  actions,
  footer,
  children,
  onClick,
  height = "100%",
}: Props) => (
  <Column
    position="relative"
    height={height}
    borderRadius="2xl"
    borderWidth="hairline"
    borderStyle="solid"
    borderColor="base.border"
    // Selection outline mirrors SelectableCard's selected treatment. Parents
    // that lay these out should pad themselves so the 2px ring isn't clipped.
    outline="2px solid"
    outlineColor={selected ? "primary.border" : "transparent"}
    // Make the card its own stacking context so the header/footer zIndex
    // (needed to sit above content with its own stacking context, e.g. an email
    // iframe) is scoped inside the card and can't paint over page-level
    // overlays. `isolation` is the purpose-built primitive for this — unlike
    // `zIndex: 0`, it doesn't silently stop working if `position: relative` is
    // ever removed. It's paint-order only (no compositing layer), so it has no
    // perf cost here.
    isolation="isolate"
    overflow="hidden"
    // No click handler (e.g. a capped, unselected card) → don't imply the body
    // is interactive.
    cursor={onClick ? "pointer" : "default"}
    userSelect="none"
    onClick={onClick}
  >
    {/* Persistent header over the media: bulk-select checkbox on the left,
        caller-supplied actions on the right. Translucent so the media stays
        partly visible behind it. Skipped entirely in single-select mode when
        there are no actions — nothing would render. */}
    {(!hideCheckbox || actions) && (
      <Row
        position="absolute"
        top={0}
        left={0}
        right={0}
        align="center"
        justify="space-between"
        gap={1}
        px={3}
        py={2}
        bg="rgba(255, 255, 255, 0.7)"
        // backdrop-filter only re-rasterizes when its backdrop moves, so it
        // stays cheap over static media. A backdrop-filter element isn't
        // clipped by the card's rounded overflow, so round its own top corners
        // to match.
        backdropFilter="blur(10px)"
        borderTopRadius="2xl"
        overflow="hidden"
        zIndex={1}
      >
        <Row
          align="center"
          gap={2}
          minWidth={0}
          onClick={(event) => event.stopPropagation()}
        >
          {!hideCheckbox && (
            <Checkbox
              aria-label={checkboxLabel}
              isChecked={selected}
              isDisabled={checkboxDisabled}
              onChange={onToggleSelect}
            />
          )}
        </Row>

        {actions && (
          <Row
            align="center"
            gap={1}
            flexShrink={0}
            onClick={(event) => event.stopPropagation()}
          >
            {actions}
          </Row>
        )}
      </Row>
    )}

    {children}

    {/* Persistent footer on the same translucent background as the header. */}
    {footer && (
      <Column
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        zIndex={1}
        gap={1}
        px={3}
        py={2}
        bg="rgba(255, 255, 255, 0.7)"
        backdropFilter="blur(10px)"
        borderBottomRadius="2xl"
      >
        {footer}
      </Column>
    )}
  </Column>
);
